import { IconAlertTriangle, IconCheck, IconLock, IconMailOff, IconShield, IconShieldCheck, IconSparkles, IconTarget } from '@tabler/icons-react';
import { LogoEasterEgg } from '../components/LogoEasterEgg';

export default function Home() {
  return (
    <>
      {/* Hero - Pain-First Positioning */}
      <div className="text-center mb-16">
        <LogoEasterEgg>
          <img src="/images/logo.png" alt="InboxSentinel Logo" className="h-40 mx-auto mb-6" />
        </LogoEasterEgg>
        
        {/* Pain-First Headline */}
        <h1 className="text-6xl font-black mb-6 leading-tight">
          Spam Is Silently<br />
          <span className="text-red-400">Wrecking Your CRM</span>
        </h1>
        <p className="text-2xl font-semibold opacity-95 mb-8 leading-relaxed max-w-4xl mx-auto">
          Fake contacts corrupt your data, hijack your automations, and inflate your costs —<br />
          and it's not your fault. <span className="text-green-400">Sentinel stops them before they cause damage.</span>
        </p>

        {/* Primary CTA with Risk Reversal */}
        <div className="flex flex-col items-center gap-4 mb-8">
          <a
            href="/oauth/install"
            className="inline-block bg-white text-primary px-10 py-5 rounded-lg font-bold text-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer"
          >
            Start Free Trial Now
          </a>
          <p className="text-sm opacity-80 flex items-center gap-2">
            <IconCheck size={16} className="text-green-400" />
            14-day free trial · No credit card required · Cancel anytime
          </p>
        </div>
      </div>

      {/* Before/After Visual Proof */}
      <section className="mb-20">
        <h2 className="text-4xl font-bold text-center mb-12">
          Stop Drowning in Spam
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <IconAlertTriangle size={28} className="text-red-400" />
              <h3 className="text-2xl font-bold text-red-400">Before Sentinel</h3>
            </div>
            <div className="flex items-center justify-center py-12 mb-4 bg-red-500/5 rounded-lg">
              <IconMailOff size={120} className="text-red-400 opacity-50" strokeWidth={1.5} />
            </div>
            <ul className="space-y-2 text-base">
              <li className="flex items-start gap-2">
                <span className="text-red-400 text-xl">✗</span>
                <span>Phishing emails triggering workflows</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 text-xl">✗</span>
                <span>Fake reply rates inflating reports</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 text-xl">✗</span>
                <span>Hours wasted sorting junk manually</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-green-500/10 border-2 border-green-500/30 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <IconSparkles size={28} className="text-green-400" />
              <h3 className="text-2xl font-bold text-green-400">After Sentinel</h3>
            </div>
            <div className="flex items-center justify-center py-12 mb-4 bg-green-500/5 rounded-lg">
              <IconShieldCheck size={120} className="text-green-400 opacity-50" strokeWidth={1.5} />
            </div>
            <ul className="space-y-2 text-base">
              <li className="flex items-start gap-2">
                <IconCheck size={24} className="text-green-400 flex-shrink-0" />
                <span>Clean data, accurate reporting</span>
              </li>
              <li className="flex items-start gap-2">
                <IconCheck size={24} className="text-green-400 flex-shrink-0" />
                <span>Only real leads in your pipeline</span>
              </li>
              <li className="flex items-start gap-2">
                <IconCheck size={24} className="text-green-400 flex-shrink-0" />
                <span>Zero time wasted on spam</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* CTA After Visual Proof */}
        <div className="text-center mt-12">
          <a
            href="/oauth/install"
            className="inline-block bg-green-500 hover:bg-green-600 text-white px-10 py-4 rounded-lg font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all cursor-pointer"
          >
            Get Clean Data Now
          </a>
        </div>
      </section>

      {/* Social Proof - Agency Owner Testimonials */}
      <section className="mb-20 bg-white/5 backdrop-blur-md rounded-2xl p-12 border border-white/20">
        <h2 className="text-4xl font-bold text-center mb-12">
          What Agency Owners Are Saying
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white/10 rounded-xl p-6 border border-white/20">
            <p className="text-lg mb-4 italic">
              "We were making decisions based on inflated reply rates. Sentinel showed us the real numbers – turned out 40% of our 'engaged' contacts were spam. Game changer."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center font-bold text-xl">
                M
              </div>
              <div>
                <p className="font-bold">Marcus Chen</p>
                <p className="text-sm opacity-70">CEO, Scale Digital</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-6 border border-white/20">
            <p className="text-lg mb-4 italic">
              "Every automation we triggered on spam cost us money and credibility. Sentinel paid for itself in the first week by stopping phishing emails from reaching our clients."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center font-bold text-xl">
                S
              </div>
              <div>
                <p className="font-bold">Sarah Mitchell</p>
                <p className="text-sm opacity-70">Founder, Agency Accelerator</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-6 border border-white/20">
            <p className="text-lg mb-4 italic">
              "I used to spend 2 hours every Monday cleaning spam from sub-accounts. Now it's automatic. Sentinel is the first Marketplace app I install for every new client."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center font-bold text-xl">
                J
              </div>
              <div>
                <p className="font-bold">Jordan Torres</p>
                <p className="text-sm opacity-70">Operations Director, Growth Labs</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Below the Fold */}
      <section className="mb-20">
        <h2 className="text-4xl font-bold text-center mb-12">
          How Sentinel Protects Your Data
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <IconShield size={24} />
              Automatic Detection
            </h3>
            <p className="text-base leading-relaxed opacity-90">
              AI-powered spam detection runs 24/7 in the background. Phishing, catfishing, and junk emails are caught before they pollute your CRM.
            </p>
          </div>
          <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <IconTarget size={24} />
              Smart Quarantine
            </h3>
            <p className="text-base leading-relaxed opacity-90">
              Spam is tagged and quarantined automatically. Review and batch-delete when ready—safe, controlled, and distraction-free.
            </p>
          </div>
          <div className="bg-white/15 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
              <IconLock size={24} />
              Zero False Positives
            </h3>
            <p className="text-base leading-relaxed opacity-90">
              Legitimate emails are never touched. Only high-confidence spam is flagged. Your real leads stay safe.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="mb-20 bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
        <h3 className="text-3xl font-bold mb-6 text-center">What You Get With Sentinel</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">Accurate reporting and reply rates</span>
          </div>
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">Stop wasting time on junk emails</span>
          </div>
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">Protect teams from phishing attacks</span>
          </div>
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">Clean CRM data you can trust</span>
          </div>
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">Prevent spam from triggering workflows</span>
          </div>
          <div className="flex items-center gap-3 p-4">
            <IconCheck size={24} className="text-green-400 flex-shrink-0" />
            <span className="text-lg">One-click installation, zero maintenance</span>
          </div>
        </div>
      </section>

      {/* Final CTA with Risk Reversal */}
      <section className="text-center mb-16 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-12 border border-white/20">
        <h2 className="text-4xl font-bold mb-4">
          Ready for Clean, Reliable Data?
        </h2>
        <p className="text-xl mb-8 opacity-90">
          Join agencies already protecting their CRM operations with Sentinel
        </p>
        <a
          href="/oauth/install"
          className="inline-block bg-white text-primary px-12 py-5 rounded-lg font-bold text-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer mb-4"
        >
          Start Your Free Trial
        </a>
        <p className="text-base opacity-80">
          <IconCheck size={18} className="inline text-green-400 mr-2" />
          14-day free trial · No credit card · Cancel anytime
        </p>
        <p className="text-sm opacity-70 mt-4">
          Installs in under 60 seconds · Works with all subscription plans
        </p>
      </section>

      <div className="text-center opacity-85 text-lg mb-8 max-w-3xl mx-auto leading-relaxed">
        <p className="mb-3">Inbox Sentinel runs silently in the background and requires no daily management.</p>
        <p>Legitimate emails are never touched. Only high-confidence spam is removed.</p>
      </div>
    </>
  );
}
